<template>
  <div class="list-inventory-dialog-container">
    <q-dialog
      ref="listInventoryDialog"
      v-cloak
      :full-width="windowWidth <= 1025"
      :position="windowWidth <= 1025 ? 'bottom' : 'standard'"
    >
      <q-card class="bg-background-light" style="width: 1000px;">
        <q-btn
          round
          dense
          icon="sym_r_close"
          :ripple="false"
          v-close-popup
          class="--icon-button --bordered --shadow__bottom"
          color="background-light"
          style="position: absolute; top: 16px; right: 16px; z-index: 1000"
        />
        <section class="list-inventory">
          <q-card class="hero-content q-pa-xl">
            <h1 class="hero-title">
              Sell more cars to
              <span class="text-secondary">motivated buyers</span>.
            </h1>

            <p class="hero-description text-text-secondary">
              Get your inventory in front of shoppers who already know what they
              want. No lead vendors, no junk traffic — just real buyers actively
              searching your market.
            </p>

            <div class="hero-actions">
              <q-btn
                unelevated
                no-caps
                label="Add My Inventory"
                icon-right="sym_r_arrow_forward"
                color="secondary"
                class="--large"
                @click="openMailto"
              />
              <q-btn
                outline
                no-caps
                label="See pricing"
                color="text-primary"
                class="--large"
                @click="scrollToPricing"
              />
            </div>
          </q-card>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-value">6.2M+</div>
              <div class="stat-label text-text-secondary">Monthly Visitors</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">167M+</div>
              <div class="stat-label text-text-secondary">
                Monthly Vehicles Views
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-value">4.1M+</div>
              <div class="stat-label text-text-secondary">
                Monthly Vehicles Clicked
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-value">63%</div>
              <div class="stat-label text-text-secondary">
                Returning Visitor Rate
              </div>
            </div>
          </div>

          <div ref="pricingSection" class="pricing-section">
            <h2 class="pricing-title">
              Pay only for clicks that reach your website
            </h2>
            <p class="pricing-subtitle text-text-secondary">
              No monthly fees, no setup costs. Every listing links directly to
              your VDP — you only pay when a real buyer clicks through.
            </p>

            <div class="pricing-charts-container">
              <div class="pricing-chart">
                <div class="pricing-header">
                  <div class="pricing-label">
                    <div class="label-small text-text-secondary">
                      LOW END CPC
                    </div>
                    <div class="label-value">$0.09</div>
                  </div>
                  <div class="pricing-label pricing-label-center">
                    <div class="label-small text-text-secondary">
                      AVERAGE CPC
                    </div>
                    <div class="label-value text-primary">$0.37</div>
                  </div>
                  <div class="pricing-label pricing-label-right">
                    <div class="label-small text-text-secondary">
                      HIGH END CPC
                    </div>
                    <div class="label-value">$1.23</div>
                  </div>
                </div>

                <div class="chart-container">
                  <div class="chart-bar" style="height: 35%"></div>
                  <div class="chart-bar" style="height: 55%"></div>
                  <div
                    class="chart-bar chart-bar-highlighted"
                    style="height: 80%"
                  >
                    <div class="chart-tooltip">Avg $0.37</div>
                  </div>
                  <div class="chart-bar" style="height: 65%"></div>
                  <div class="chart-bar" style="height: 50%"></div>
                  <div class="chart-bar" style="height: 45%"></div>
                  <div class="chart-bar" style="height: 40%"></div>
                  <div class="chart-bar" style="height: 35%"></div>
                  <div class="chart-bar" style="height: 30%"></div>
                  <div class="chart-bar" style="height: 25%"></div>
                </div>

                <div class="chart-labels">
                  <span>$0.09</span>
                  <span>$0.40</span>
                  <span>$0.70</span>
                  <span>$1.00</span>
                  <span>$1.15</span>
                  <span>$1.23</span>
                </div>

                <div class="pricing-note">
                  <q-icon name="sym_r_info" size="20px" />
                  <p class="text-text-secondary">
                    CPC varies by vehicle category, location, and competition.
                    Most dealers average
                    <strong class="text-text-primary">$0.37 per click</strong> —
                    you only pay when a buyer clicks through to your site.
                  </p>
                </div>
              </div>

              <q-separator inset class="q-my-lg" />

              <div class="pricing-chart">
                <div class="pricing-header">
                  <div class="pricing-label">
                    <div class="label-small text-text-secondary">
                      LOW END CPL
                    </div>
                    <div class="label-value">$5</div>
                  </div>
                  <div class="pricing-label pricing-label-center">
                    <div class="label-small text-text-secondary">
                      AVERAGE CPL
                    </div>
                    <div class="label-value text-primary">$14</div>
                  </div>
                  <div class="pricing-label pricing-label-right">
                    <div class="label-small text-text-secondary">
                      HIGH END CPL
                    </div>
                    <div class="label-value">$41</div>
                  </div>
                </div>

                <div class="chart-container">
                  <div class="chart-bar" style="height: 25%"></div>
                  <div class="chart-bar" style="height: 45%"></div>
                  <div
                    class="chart-bar chart-bar-highlighted"
                    style="height: 70%"
                  >
                    <div class="chart-tooltip">Avg $14</div>
                  </div>
                  <div class="chart-bar" style="height: 55%"></div>
                  <div class="chart-bar" style="height: 40%"></div>
                  <div class="chart-bar" style="height: 35%"></div>
                  <div class="chart-bar" style="height: 30%"></div>
                  <div class="chart-bar" style="height: 25%"></div>
                  <div class="chart-bar" style="height: 20%"></div>
                  <div class="chart-bar" style="height: 15%"></div>
                </div>

                <div class="chart-labels">
                  <span>$5</span>
                  <span>$13</span>
                  <span>$23</span>
                  <span>$33</span>
                  <span>$38</span>
                  <span>$41</span>
                </div>

                <div class="pricing-note">
                  <q-icon
                    name="sym_r_info"
                    size="18px"
                    class="text-text-secondary"
                  />
                  <p class="text-text-secondary">
                    Average CPL is based on our platform average of a
                    <strong class="text-text-primary"
                      >3% click-to-lead conversion rate</strong
                    >
                    — at the average CPC that works out to about
                    <strong class="text-text-primary">$14 per lead</strong>.
                    Your actual CPL depends on your VDP experience.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="features-section">
            <h2 class="features-title">
              Everything you need to move inventory
            </h2>
            <p class="features-subtitle text-text-secondary">
              Built for sellers who want real performance, not vanity metrics.
            </p>

            <div class="features-grid">
              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_visibility" size="32px" />
                </div>
                <h3 class="feature-title">Premium placement</h3>
                <p class="feature-description text-text-secondary">
                  Push your inventory to the top of search results and reach
                  buyers actively looking for your inventory.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_ads_click" size="32px" />
                </div>
                <h3 class="feature-title">Targeted reach</h3>
                <p class="feature-description text-text-secondary">
                  Show your listings to shoppers filtering by your make, model,
                  body type, price range, and ZIP radius.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_query_stats" size="32px" />
                </div>
                <h3 class="feature-title">Real-time analytics</h3>
                <p class="feature-description text-text-secondary">
                  Track impressions, clicks, visitors, CTR, and time-on-page for
                  every listing in your inventory.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_storefront" size="32px" />
                </div>
                <h3 class="feature-title">Seller Branding</h3>
                <p class="feature-description text-text-secondary">
                  Showcase your logo/brand across every listing so shoppers
                  recognize you before they ever click.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_open_in_new" size="32px" />
                </div>
                <h3 class="feature-title">Direct traffic to your site</h3>
                <p class="feature-description text-text-secondary">
                  Every listing click sends the buyer straight to your website —
                  your customer, your funnel, your sale.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_notifications" size="32px" />
                </div>
                <h3 class="feature-title">Visitor Alerts</h3>
                <p class="feature-description text-text-secondary">
                  Returning visitors are notified when your inventory matches
                  their search preferences — so you can follow up while they're
                  still in-market.
                </p>
              </div>
            </div>
          </div>

          <div class="features-section">
            <h2 class="features-title">We use AI when the data supports it</h2>
            <p class="features-subtitle text-text-secondary">
              Every vehicle and impression is scored by our targeting model — so
              your budget follows real purchase intent.
            </p>

            <div class="features-grid">
              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_auto_awesome" size="32px" />
                </div>
                <h3 class="feature-title">Intent Modeling</h3>
                <p class="feature-description text-text-secondary">
                  We score your inventory data, live browsing, search history,
                  and saved behavior to find shoppers most likely to buy models
                  you're selling.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_group" size="32px" />
                </div>
                <h3 class="feature-title">Lookalike matching</h3>
                <p class="feature-description text-text-secondary">
                  Your listings surface to buyers who resemble the shoppers
                  who've already shown interest on similar inventory.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">
                  <q-icon name="sym_r_tune" size="32px" />
                </div>
                <h3 class="feature-title">Continuous optimization</h3>
                <p class="feature-description text-text-secondary">
                  The model shifts your impressions toward the listings,
                  audiences, and locations performing best — in real time.
                </p>
              </div>
            </div>
          </div>

          <div class="cta-section">
            <div class="cta-content">
              <h2 class="cta-title">
                Ready to put your inventory in front of buyers?
              </h2>
              <p class="cta-subtitle text-text-secondary">
                Onboarding only takes a few minutes — most sellers are live in
                less than 24 hours.
              </p>
            </div>
            <q-btn
              unelevated
              no-caps
              label="Add My Inventory"
              icon-right="sym_r_arrow_forward"
              color="secondary"
              class="--large"
              @click="openMailto"
            />
          </div>
        </section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
export default {
  name: "ListInventoryDialog",
  components: {},
  data() {
    return {};
  },
  methods: {
    show() {
      this.$refs.listInventoryDialog.show();
    },
    hide() {
      this.$refs.listInventoryDialog.hide();
    },
    scrollToPricing() {
      this.$refs.pricingSection.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    },
    openMailto() {
      window.location.href = "mailto:support@carscience.com";
    }
  }
};
</script>

<style scoped lang="scss">
.list-inventory {
  padding: $space-lg;
  margin: 0 auto;
}

.hero-title {
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 20px 0;
}

.hero-description {
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 32px 0;
  max-width: 700px;
}

.hero-actions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 40px;
}

.stat-card {
  padding: 20px 24px;
  background: color(background, light);
  border-radius: $border-radius;
  border: 1px solid color(border);
  box-shadow: $shadow-bottom;
}

.stat-value {
  font-size: 40px;
  font-weight: 700;
  color: color(text);
  line-height: 1;
  margin-bottom: 6px;
}

.stat-label {
  font-size: 14px;
  line-height: 1;
  font-weight: 500;
  color: color(text, light);
}

.pricing-section {
  margin-top: 32px;
}

.pricing-title {
  font-size: 27px;
  font-weight: 700;
  margin: 0 0 2px 0;
  color: color(text);
  line-height: 1.4;
}

.pricing-subtitle {
  font-size: 16px;
  line-height: 1.4;
  color: color(text, light);
}

.pricing-charts-container {
  border: 1px solid color(border);
  box-shadow: $shadow-bottom;
  padding: 24px;
  border-radius: $border-radius;
  margin-top: 16px;
}
.pricing-chart {
  &:last-child {
    margin-bottom: 0;
  }
}

.pricing-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.pricing-label {
  text-align: left;
  color: color(text, light);

  &.pricing-label-center {
    text-align: center;
  }

  &.pricing-label-right {
    text-align: right;
  }
}

.label-small {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.label-value {
  font-size: 32px;
  font-weight: 700;
  color: color(text);
}

.chart-container {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 200px;
  margin-bottom: 8px;
  padding: 0 4px;
}

.chart-bar {
  flex: 1;
  background: rgba(231, 23, 27, 0.3);
  border-radius: 4px 4px 0 0;
  position: relative;
  transition: background 0.2s;

  &.chart-bar-highlighted {
    background: color(secondary);
  }
}

.chart-tooltip {
  position: absolute;
  top: -32px;
  left: 10%;
  transform: translateX(-50%);
  background: color(text);
  color: color(background);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid color(text);
  }
}

.chart-labels {
  display: flex;
  justify-content: space-between;
  padding: 0 4px;
  font-size: 12px;
  color: color(text, light);
  margin-bottom: 12px;
}

.pricing-note {
  display: flex;
  gap: 8px;
  align-items: center;

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.3;
  }
}

.features-section {
  margin-top: 32px;
}

.features-title {
  font-size: 27px;
  font-weight: 700;
  margin: 0 0 2px 0;
  color: color(text);
  line-height: 1.4;
}

.features-subtitle {
  font-size: 16px;
  line-height: 1.4;
  color: color(text, light);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 16px;
}

.feature-card {
  padding: 24px;
  background: color(background, light);
  border-radius: $border-radius;
  border: 1px solid color(border);
  box-shadow: $shadow-bottom;
}

.feature-icon {
  width: 56px;
  height: 56px;
  background: rgba(color(secondary), 0.85);
  border-radius: $border-radius;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: color(text);
}

.feature-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: color(text);
  line-height: 1.4;
}

.feature-description {
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

.cta-section {
  margin-top: 32px;
  padding: 32px;
  background: color(background);
  border-radius: $border-radius;
  border: 1px solid color(border);
  box-shadow: $shadow-bottom;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}

.cta-content {
  flex: 1;
}

.cta-title {
  font-size: 25px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: color(text);
  line-height: 1.4;
}

.cta-subtitle {
  font-size: 16px;
  line-height: 1.4;
  margin: 0;
  color: color(text, light);
}

@media (max-width: 1025px) {
  .list-inventory {
    padding: 32px 24px;
  }

  .hero-title {
    font-size: 36px;
  }

  .hero-description {
    font-size: 16px;
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .stat-value {
    font-size: 32px;
  }

  .pricing-section {
    margin-top: 60px;
  }

  .pricing-title {
    font-size: 24px;
  }

  .pricing-subtitle {
    font-size: 14px;
  }

  .pricing-chart {
    margin-bottom: 48px;
  }

  .pricing-header {
    margin-bottom: 16px;
  }

  .label-value {
    font-size: 24px;
  }

  .chart-container {
    height: 150px;
    gap: 4px;
  }

  .chart-labels {
    font-size: 10px;
  }

  .features-section {
    margin-top: 60px;
  }

  .features-title {
    font-size: 24px;
  }

  .features-subtitle {
    font-size: 14px;
    margin-bottom: 32px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .feature-card {
    padding: 24px;
  }

  .cta-section {
    margin-top: 60px;
    padding: 32px 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }

  .cta-title {
    font-size: 22px;
  }

  .cta-subtitle {
    font-size: 14px;
  }
}
</style>
